import { Component, OnInit, Input, Output, ViewChild, EventEmitter, Inject, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, ElementRef, OnDestroy, NgZone, OnChanges, SimpleChanges } from '@angular/core';
import { StoreMenuItemsV2, MenuItemGroup, Store } from 'src/app/models/store';
import { ActivatedRoute } from '@angular/router';
import { OrdersWithPayment, bestListThumbnail } from 'src/app/models/order';
import { OrderService } from 'src/app/order.service';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MenuItemBottomSheetComponent, MenuItemBottomSheetData } from 'src/app/menu-item/menu-item-bottom-sheet/menu-item-bottom-sheet.component';
import { ReorderSheetComponent, ReorderSheetData } from 'src/app/store-lazy/reorder-sheet/reorder-sheet.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnalyticsService } from 'src/app/_services/analytics.service';
import { DEVICE_TOKEN_KEY } from 'src/app/_helpers/auth.interceptor';

export type MenuLayout = 'list' | 'grid';

@Component({
  selector: 'store-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  displayedColumns: string[] = ['picture', 'item', 'price'];
  @Input() orderId: string;
  @Input() storeMenu: StoreMenuItemsV2;
  @Input() store: Store;
  @Input() orderWithPayment: OrdersWithPayment;
  @Input() filter: string;
  @Input() openAll;
  @Input() isCatering: boolean = false;
  @Input() searchScrolledOut: boolean = false;
  @Output() emitLazy
  @Output() orderUpdated = new EventEmitter<void>();
  @Output() filterChanged = new EventEmitter<string>();
  /** True while the phone chip bar owns search, so the parent can hide its own search row. */
  @Output() compactSearch = new EventEmitter<boolean>();
  @ViewChild('barSearchInput') barSearchInputRef: ElementRef<HTMLInputElement>;
  barSearchOpen = false;

  @ViewChild('categoryBar') categoryBarRef: ElementRef;
  @ViewChild('categorySidebar') categorySidebarRef: ElementRef;

  isEnd = false
  limit = 50
  offset = 0
  selectedCategoryFragment;
  selectedSubCategoryFragment;
  uiSetting: any;
  activeCategory: string = '';
  activeSubCategory: string = '';
  isDesktop = false;
  /**
   * Which categories / sub-categories are open. Seeded from the store's "Expand menu category
   * by default" and "Expand menu subcategory by default" settings (business console → Menu
   * settings): with them off the shopper lands on the list of category headings and opens the
   * one they want, which is how the menu behaved before the photo grid landed.
   */
  expandedCategories = new Set<string>();
  expandedSubCategories = new Set<string>();
  private expandCategoriesByDefault = true;
  private expandSubCategoriesByDefault = true;
  /** Sticky offset for the chip bar: below the sticky search row on mobile, top of viewport on desktop. */
  barTop = 0;
  categoryImages: { [key: string]: string } = {};
  subCategoryImages: { [key: string]: string } = {};
  /** Visible item count per top-level category (sidebar + section headings). */
  categoryCounts: { [key: string]: number } = {};
  /** True when a category hit the page cap, so the count is a floor ("50+ items"). */
  private categoryCountCapped: { [key: string]: boolean } = {};
  /**
   * One layout for every store so the menu reads as one design: image-first grid cards
   * everywhere. Items without a photo get an on-brand placeholder tile, so a card keeps
   * the same shape whether or not the merchant has uploaded artwork yet.
   */
  storeLayout: MenuLayout = 'grid';
  private layoutBySubgroup: { [subKey: string]: MenuLayout } = {};
  /** Desktop chip bar arrows: which directions still have hidden chips. */
  readonly imageBase = 'https://storage.googleapis.com/menus/';

  private scrollTicking = false;
  private isScrollSpyEnabled = true;
  private scrollSpyResumeTimer: any = null;
  private destroyed = false;
  private desktopQuery: MediaQueryList;
  /** Re-measures the sticky stack whenever the header or search row actually changes height. */
  private stickyResizeObs: ResizeObserver | null = null;

  constructor(
    private orderService: OrderService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private document,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private overlay: Overlay,
    private snackBar: MatSnackBar,
    private analyticsService: AnalyticsService
  ) { }

  /** Item-group ids with a quick-add in flight (drives the button spinner). */
  addingIds = new Set<number>();
  /** Quantity already in the order per item-group id, for the "+" badge. */
  cartCounts: { [groupId: number]: number } = {};

  private rebuildCartCounts() {
    const counts: { [groupId: number]: number } = {};
    for (const line of this.orderWithPayment?.items || []) {
      if (line.is_removed) continue;
      const gid = line.menu_item?.menu_item_group;
      if (gid == null) continue;
      counts[gid] = (counts[gid] || 0) + (+line.quantity || 0);
    }
    this.cartCounts = counts;
  }

  /**
   * "+" on a card. Adds straight to the order when the item has one available portion and no
   * required modifier group; otherwise opens the options dialog with that portion preselected.
   */
  quickAdd(item: any) {
    if (!item || this.addingIds.has(item.id)) return;
    if (!this.orderId || !this.store?.isAvailable()) {
      this.openItemBottomSheet(item);
      return;
    }
    this.addingIds.add(item.id);
    this.cdr.markForCheck();
    const done = () => { this.addingIds.delete(item.id); this.cdr.markForCheck(); };

    this.orderService.getMenuItemGroupOptionsForStore(this.store.id, String(item.id), this.isCatering).subscribe(options => {
      let portions = (options || []).filter(m => !m.is_pos_only && m.is_available);
      if (this.isCatering) portions = portions.filter(m => m.show_on_catering_menu !== false);
      if (portions.length !== 1) {
        done();
        this.openItemBottomSheet(item);
        return;
      }
      const portion = portions[0];
      this.orderService.getModifiersForMenuItem(this.store.id, String(portion.id)).subscribe(data => {
        const needsChoice = (data?.modifier_groups || []).some(g => g.is_required);
        if (needsChoice) {
          done();
          this.openItemBottomSheet(item, portion.id);
          return;
        }
        // Quick-add skips the item dialog entirely, so it has to report add_to_cart itself.
        this.analyticsService.trackEvent('add_to_cart', {
          item_id: portion.id,
          item_name: item.name,
          price: portion.base_price,
          quantity: 1,
          store_id: this.store?.id
        });
        this.orderService.addnewItem(this.orderId, String(portion.id), [], 1, '').subscribe(() => {
          done();
          // No toast on success. It sat at the bottom of the screen on top of the View Order bar
          // and its action was a second "View order" for the button it was covering. The count on
          // the item's own + button and the bar's total already confirm the add.
          this.orderUpdated.emit();
        }, () => {
          done();
          this.snackBar.open('Could not add item. Please try again.', 'OK',
                             { duration: 3000, verticalPosition: 'top' });
        });
      }, () => { done(); this.openItemBottomSheet(item, portion.id); });
    }, () => { done(); this.openItemBottomSheet(item); });
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler($event: any): void {
    localStorage.removeItem('menu_group_name');
    localStorage.removeItem('menu_sub_group_name');
  }

  ngOnInit() {
    this.uiSetting = this.store.ui_settings;
    this.route.fragment.subscribe(fragment => {
      this.selectedCategoryFragment = fragment;
      this.cdr.markForCheck();
    });
    for (let key in this.storeMenu.menu_items) {
      let value = this.storeMenu.menu_items[key];
      value.setCurrentOffset = 0
      value.disabled = false;
    }

    /* Click Back Behaviour */
    const selectedMenuGroup = localStorage.getItem('menu_group_name');
    const selectedSubMenuGroup = localStorage.getItem('menu_sub_group_name');
    if (selectedMenuGroup) {
      this.selectedCategoryFragment = selectedMenuGroup;
      if (selectedSubMenuGroup) {
        this.selectedSubCategoryFragment = selectedSubMenuGroup
      } else {
        this.selectedSubCategoryFragment = null;
      }
    } else {
      this.selectedCategoryFragment = null;
    }

    this.initExpandedState();

    // Build category image map from menu items
    this.buildCategoryImages();

    // Initialize active category to first one
    if (this.storeMenu?.menu_order?.length) {
      this.activeCategory = this.storeMenu.menu_order[0][0];
    }

    // Desktop detection
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.isDesktop = this.desktopQuery.matches;
    this.desktopQuery.addEventListener('change', this.onDesktopChange);
    this.publishCompactSearch();
  }

  /**
   * Reads the two store settings and opens whatever they ask for. A menu with a single category
   * has no choice to offer, so it always opens; a search is always shown open, otherwise the
   * matches would hide behind closed headings.
   */
  private initExpandedState() {
    const order = this.storeMenu?.menu_order || [];
    this.expandCategoriesByDefault = !!this.store?.isEnabled('collapse_menu_category') || order.length <= 1;
    this.expandSubCategoriesByDefault = !!this.store?.isEnabled('collapse_menu_subcategory');
    this.expandedCategories.clear();
    this.expandedSubCategories.clear();
    for (const itemGroup of order) {
      if (this.expandCategoriesByDefault) this.expandedCategories.add(itemGroup[0]);
      if (this.expandSubCategoriesByDefault) {
        for (const sub of itemGroup[1] || []) {
          if (sub) this.expandedSubCategories.add(this.getSubCategoryId(itemGroup[0], sub));
        }
      }
    }

    // A shared link (#Category) or the category the shopper was last browsing should arrive
    // open; landing on a closed heading reads as if the link went nowhere.
    const target = this.route.snapshot.fragment || localStorage.getItem('menu_group_name');
    if (target) {
      this.expandedCategories.add(target);
      const sub = localStorage.getItem('menu_sub_group_name');
      if (sub) this.expandedSubCategories.add(this.getSubCategoryId(target, sub));
    }
  }

  /** Headings only carry a chevron when there is something to choose between. */
  get categoriesCollapsible(): boolean {
    return !this.filter && (this.storeMenu?.menu_order?.length || 0) > 1;
  }

  subCategoriesCollapsible(itemGroup: any): boolean {
    return !this.filter && this.hasMultipleSubCategories(itemGroup);
  }

  isCategoryOpen(categoryName: string): boolean {
    return !this.categoriesCollapsible || this.expandedCategories.has(categoryName);
  }

  isSubCategoryOpen(itemGroup: any, subgroup: string): boolean {
    if (!subgroup || !this.subCategoriesCollapsible(itemGroup)) return true;
    return this.expandedSubCategories.has(this.getSubCategoryId(itemGroup[0], subgroup));
  }

  /**
   * Category heading tap. Opening pulls the heading up under the sticky bar so the items land
   * directly under the thing that was tapped; closing keeps that heading exactly where it is.
   */
  toggleCategory(categoryName: string) {
    if (!this.categoriesCollapsible) return;
    if (this.expandedCategories.has(categoryName)) {
      this.closeAnchored(categoryName, () => this.expandedCategories.delete(categoryName));
      return;
    }
    this.onChangeMenu(categoryName);
    this.scrollToCategory(categoryName);
  }

  toggleSubCategory(itemGroup: any, subgroup: string) {
    if (!this.subCategoriesCollapsible(itemGroup)) return;
    const subId = this.getSubCategoryId(itemGroup[0], subgroup);
    if (this.expandedSubCategories.has(subId)) {
      this.closeAnchored(subId, () => this.expandedSubCategories.delete(subId));
      return;
    }
    this.scrollToSubCategory(itemGroup[0], subgroup);
  }

  /**
   * Closing a section deletes every card under its heading, so the document shrinks while the
   * scroll position stays put and the page appears to jump. Measure the heading before and after
   * the collapse and give back the difference, which pins it under the finger that tapped it.
   */
  private closeAnchored(elementId: string, mutate: () => void) {
    const el = this.document.getElementById(elementId) as HTMLElement | null;
    const before = el ? el.getBoundingClientRect().top : null;

    // Spy off across the correction: the programmatic scroll below is not the shopper browsing.
    this.suspendScrollSpy();
    mutate();
    this.syncView();

    if (el && before !== null) {
      const delta = el.getBoundingClientRect().top - before;
      // `styles.scss` sets `html { scroll-behavior: smooth }`, and the default 'auto' obeys it —
      // which would animate a correction whose whole job is to be invisible. Force it instant.
      if (delta) window.scrollBy({ top: delta, behavior: 'instant' as ScrollBehavior });
    }
    this.resumeScrollSpyWhenIdle();
  }

  /** Render pending state now, because the next line measures the DOM it produces. */
  private syncView() {
    if (this.destroyed) return;
    this.cdr.detectChanges();
  }

  /** Also drops any pending resume, so a second tap is not re-enabled by the first tap's timer. */
  private suspendScrollSpy() {
    clearTimeout(this.scrollSpyResumeTimer);
    this.scrollSpyResumeTimer = null;
    this.isScrollSpyEnabled = false;
  }

  /**
   * `scrollIntoView({behavior:'smooth'})` takes as long as the distance demands, so the old fixed
   * 500ms timer switched scroll-spy back on mid-flight: the chip bar then raced through every
   * category the animation passed and dragged the sidebar along with it. Wait for the page to
   * actually stop moving instead, with a ceiling so a cancelled scroll cannot leave spy off.
   */
  private resumeScrollSpyWhenIdle() {
    clearTimeout(this.scrollSpyResumeTimer);
    let lastY = window.scrollY;
    let still = 0;
    let waited = 0;

    // Outside the zone: this only flips a private flag, and a change-detection pass every 60ms
    // for the length of a smooth scroll is exactly the jank we are trying to remove.
    this.ngZone.runOutsideAngular(() => {
      const check = () => {
        if (this.destroyed) return;
        const y = window.scrollY;
        if (y === lastY) { still++; } else { still = 0; lastY = y; }
        waited += 60;
        if (still >= 3 || waited >= 3000) {
          this.isScrollSpyEnabled = true;
          this.scrollSpyResumeTimer = null;
          return;
        }
        this.scrollSpyResumeTimer = setTimeout(check, 60);
      };
      this.scrollSpyResumeTimer = setTimeout(check, 60);
    });
  }

  /** Named sub-categories of a category that actually have items. */
  namedSubCategories(itemGroup: any): string[] {
    const subs = ((itemGroup?.[1] as unknown) as string[]) || [];
    return subs.filter(sub => !!sub && this.storeMenu?.menu_items?.[itemGroup[0]]?.[sub]?.length);
  }

  /** Sub-category chips / labels only earn their place when there is a real choice to make. */
  hasMultipleSubCategories(itemGroup: any): boolean {
    return this.namedSubCategories(itemGroup).length >= 2;
  }

  /**
   * Sub-categories of the category currently in view, for the second chip row.
   *
   * A closed category has no sub-sections on screen to jump to, so it contributes no chips. That
   * also keeps the sticky bar a fixed height while scrolling a collapsed list: the row used to
   * appear and disappear as scroll-spy moved between categories, and because the bar sits in flow
   * every one of those height changes shoved the page under it.
   */
  get activeSubCategories(): string[] {
    const group = this.storeMenu?.menu_order?.find(g => g[0] === this.activeCategory);
    if (!group || !this.hasMultipleSubCategories(group)) return [];
    if (!this.isCategoryOpen(this.activeCategory)) return [];
    return this.namedSubCategories(group);
  }

  /** Phones: the bar's search chip replaces the parent's search row. */
  private publishCompactSearch() {
    this.compactSearch.emit(!this.isDesktop);
    setTimeout(() => this.measureBarTop());
  }

  openBarSearch() {
    this.barSearchOpen = true;
    this.cdr.markForCheck();
    setTimeout(() => this.barSearchInputRef?.nativeElement?.focus(), 50);
  }

  closeBarSearch() {
    this.barSearchOpen = false;
    if (this.filter) this.filterChanged.emit('');
    this.cdr.markForCheck();
  }

  onBarSearch(value: string) {
    this.filterChanged.emit(value);
  }

  /** Empty-state action: drop the filter and show the full menu again. */
  clearSearch() {
    this.barSearchOpen = false;
    this.filterChanged.emit('');
    this.cdr.markForCheck();
  }

  /** Distance from the viewport top to the bottom of whatever is stuck above the sections. */
  private stickyOffset(): number {
    const bar = this.categoryBarRef?.nativeElement as HTMLElement | undefined;
    const barHeight = bar ? bar.getBoundingClientRect().height : 0;
    return this.barTop + barHeight + 8;
  }

  /**
   * The threshold scroll-spy uses to decide which section is "in view" — the top row of the bar
   * only, never the sub-category row underneath it.
   *
   * That row is shown *because of* what the spy decided, so measuring through it made the answer
   * depend on itself: scrolling past the last sub-category of a category moved the next one into
   * view, the row went away, the bar got shorter, the content slid back up, the old category won
   * again and the row came back. It never settled. Reading only the fixed top row breaks the loop.
   */
  private spyOffset(): number {
    const bar = this.categoryBarRef?.nativeElement as HTMLElement | undefined;
    if (!bar) return this.barTop + 8;
    const topRow = bar.querySelector('.category-bar-row, .bar-search') as HTMLElement | null;
    return this.barTop + (topRow ? topRow.getBoundingClientRect().height : 0) + 8;
  }

  private measureBarTop() {
    // Sticky stack, top to bottom: store header → search row → chip bar / sidebar.
    // The header height is published as a CSS variable so the parent's search row can sit
    // under it; the bar/sidebar offset is header + search.
    const header = this.document.querySelector('.store-header-toolbar') as HTMLElement | null;
    const headerH = header && getComputedStyle(header).position === 'sticky'
      ? Math.round(header.getBoundingClientRect().height) : 0;
    this.document.documentElement.style.setProperty('--sticky-header-h', `${headerH}px`);
    const searchRow = this.document.querySelector('.search-row') as HTMLElement | null;
    const searchH = searchRow ? Math.round(searchRow.getBoundingClientRect().height) : 0;
    const next = headerH + searchH;
    if (next !== this.barTop) {
      this.barTop = next;
      this.cdr.markForCheck();
    }
  }

  /**
   * The header holds a remote logo and a verified badge, and the store name/description can
   * rewrap; every one of those changes its height *after* the two measurements above have run,
   * which used to leave `--sticky-header-h` too small and park the sticky search row underneath
   * the header. Watching both elements keeps the offset honest for the life of the page.
   * Setting the variable never changes either element's size, so this cannot feed back on itself.
   */
  private observeStickyStack() {
    if (typeof ResizeObserver === 'undefined') return;
    const targets = ['.store-header-toolbar', '.search-row']
      .map(sel => this.document.querySelector(sel) as HTMLElement | null)
      .filter(Boolean) as HTMLElement[];
    if (!targets.length) return;

    this.stickyResizeObs = new ResizeObserver(() => {
      this.ngZone.run(() => this.measureBarTop());
    });
    targets.forEach(el => this.stickyResizeObs.observe(el));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderWithPayment']) { this.rebuildCartCounts(); this.cdr.markForCheck(); }
    // The parent replaces `storeMenu` on every search without recreating this component,
    // so covers, counts and grid/list decisions must be rebuilt for the new item sets.
    if (changes['filter'] && !changes['filter'].firstChange) {
      // A query typed into the page-level search must stay visible once the
      // bar takes over search on phones.
      if (!this.isDesktop && this.filter) this.barSearchOpen = true;
      this.publishCompactSearch();
    }
    if (changes['storeMenu'] && !changes['storeMenu'].firstChange) {
      this.categoryImages = {};
      this.subCategoryImages = {};
      this.categoryCounts = {};
      this.categoryCountCapped = {};
      this.layoutBySubgroup = {};
      this.buildCategoryImages();
      this.initExpandedState();
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit() {
    // The sticky search row's height is only known once rendered (logo loads async).
    this.measureBarTop();
    setTimeout(() => this.measureBarTop(), 600);
    this.observeStickyStack();

    // Scroll + resize listeners run outside the Angular zone; they only touch the zone when
    // the active chip actually changes or the sticky offset moves.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize, { passive: true });
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.scrollSpyResumeTimer);
    this.closeCategoryPicker();
    this.stickyResizeObs?.disconnect();
    this.stickyResizeObs = null;
    window.removeEventListener('scroll', this.onWindowScroll);
    window.removeEventListener('resize', this.onWindowResize);
    this.desktopQuery?.removeEventListener('change', this.onDesktopChange);
  }

  private resizeTicking = false;
  private onWindowResize = () => {
    if (this.resizeTicking) return;
    this.resizeTicking = true;
    requestAnimationFrame(() => {
      this.ngZone.run(() => this.measureBarTop());
      this.resizeTicking = false;
    });
  }

  private onDesktopChange = (e: MediaQueryListEvent) => {
    this.isDesktop = e.matches;
    if (this.isDesktop) this.barSearchOpen = false;
    this.publishCompactSearch();
    this.measureBarTop();
    this.cdr.markForCheck();
  }

  private onWindowScroll = () => {
    if (!this.scrollTicking && this.isScrollSpyEnabled) {
      requestAnimationFrame(() => {
        this.updateActiveCategory();
        this.scrollTicking = false;
      });
      this.scrollTicking = true;
    }
  }

  private updateActiveCategory() {
    if (!this.storeMenu?.menu_order?.length) return;

    const scrollOffset = this.spyOffset();
    let closestCategory = this.storeMenu.menu_order[0][0];
    let closestDistance = Infinity;

    for (const itemGroup of this.storeMenu.menu_order) {
      const categoryName = itemGroup[0];
      const el = this.document.getElementById(categoryName) as HTMLElement;
      if (el) {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top - scrollOffset);
        if (rect.top <= scrollOffset + 10 && distance < closestDistance) {
          closestDistance = distance;
          closestCategory = categoryName;
        }
      }
    }

    // Track active sub-category within the active parent
    let newActiveSub = '';
    const activeGroup = this.storeMenu.menu_order.find(g => g[0] === closestCategory);
    if (activeGroup && this.hasSubCategories(activeGroup)) {
      let closestSubDist = Infinity;
      for (const sub of activeGroup[1]) {
        if (!sub) continue;
        const subId = this.getSubCategoryId(closestCategory, sub);
        const el = this.document.getElementById(subId) as HTMLElement;
        if (el) {
          const rect = el.getBoundingClientRect();
          const distance = Math.abs(rect.top - scrollOffset);
          if (rect.top <= scrollOffset + 10 && distance < closestSubDist) {
            closestSubDist = distance;
            newActiveSub = sub;
          }
        }
      }
    }

    if (closestCategory !== this.activeCategory || newActiveSub !== this.activeSubCategory) {
      this.activeCategory = closestCategory;
      this.activeSubCategory = newActiveSub;
      this.scrollPillIntoView(closestCategory);
      this.ngZone.run(() => {
        this.cdr.markForCheck();
      });
    }
  }

  /** Keep the active entry visible in the desktop sidebar (the phone picker is a <select>, nothing to scroll). */
  private scrollPillIntoView(categoryName: string) {
    const sidebar = this.categorySidebarRef?.nativeElement as HTMLElement | undefined;
    const sideItem = sidebar?.querySelector(`[data-category="${categoryName}"]`) as HTMLElement | undefined;
    if (sidebar && sideItem) {
      const top = sideItem.offsetTop - sidebar.clientHeight / 2 + sideItem.offsetHeight / 2;
      sidebar.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }


  /**
   * The shopper's id, from the route if the page carries one, else the one the app stored when
   * they last identified themselves. Only someone who has ordered here before has it, which is
   * exactly who the Reorder button is for.
   */
  get userHash(): string {
    return this.route.snapshot.paramMap.get('userHash') || localStorage.getItem('userHash') || '';
  }

  /**
   * Proof this browser completed a verification, not just that it once stored a userHash.
   * A userHash survives logout and is copyable, so on its own it would offer a stranger's
   * order history to anyone who has not verified. See auth.interceptor.ts.
   */
  private get isVerifiedDevice(): boolean {
    try {
      return !!localStorage.getItem(DEVICE_TOKEN_KEY);
    } catch (e) {
      return false;   // private window or blocked storage
    }
  }

  get canReorder(): boolean {
    return this.isVerifiedDevice && !!this.userHash
      && !!this.store?.feature_flag && !!this.store.isEnabled('reorder');
  }

  /** Recent orders in a sheet, each one tap from a fresh cart, without leaving the menu. */
  openReorderSheet() {
    const data: ReorderSheetData = {
      storeId: String(this.store.id),
      userHash: this.userHash,
      store: this.store,
    };
    this.dialog.open(ReorderSheetComponent, {
      data,
      autoFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      ...(this.isDesktop
        ? { panelClass: 'reorder-sheet-dialog', width: '460px', maxWidth: '94vw' }
        : { panelClass: 'reorder-sheet-mobile', width: '100vw', maxWidth: '100vw', position: { bottom: '0' } }),
    });
  }

  /** Phone/tablet category picker (custom list under the bar; the browser <select> popup looked out of place). */
  categoryPickerOpen = false;

  toggleCategoryPicker() {
    this.categoryPickerOpen ? this.closeCategoryPicker() : this.openCategoryPicker();
  }

  openCategoryPicker() {
    this.categoryPickerOpen = true;
    this.document.body.style.overflow = 'hidden';
    this.cdr.markForCheck();
  }

  closeCategoryPicker() {
    if (!this.categoryPickerOpen) return;
    this.categoryPickerOpen = false;
    this.document.body.style.overflow = '';
    this.cdr.markForCheck();
  }

  pickCategory(categoryName: string) {
    this.closeCategoryPicker();
    if (!categoryName) return;
    // Let the body scroll lock lift before jumping to the section.
    setTimeout(() => this.scrollToCategory(categoryName), 0);
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeCategoryPicker();
  }

  scrollToCategory(categoryName: string) {
    // A chip, the picker or the sidebar can aim at a closed section; land on its items, not its heading.
    this.expandedCategories.add(categoryName);
    this.suspendScrollSpy();
    this.activeCategory = categoryName;
    this.activeSubCategory = '';
    this.selectedCategoryFragment = categoryName;
    // Render the section first: aiming at a heading whose section is still empty lands short.
    this.syncView();
    this.scrollPillIntoView(categoryName);
    this.afterLayout(() => this.smoothScrollTo(categoryName));
  }

  openItemBottomSheet(item: any, menuItemId?: number) {
    // The menu list already carries the item's photos (`last_image`, newest first);
    // hand them to the dialog so the detail image paints without another request.
    const lastImage = item?.last_image;
    const images = Array.isArray(lastImage) ? lastImage : (lastImage ? [lastImage] : []);

    const sheetData: MenuItemBottomSheetData = {
      storeId: this.store.id,
      orderId: this.orderId,
      menuItemGroupId: item.id,
      store: this.store,
      isCatering: this.isCatering,
      images,
      description: item?.description && item.description != 0 ? String(item.description) : '',
      presentation: this.isDesktop ? 'modal' : 'sheet',
      ...(menuItemId ? { menuItemId: String(menuItemId) } : {}),
    };

    // Mobile: full-screen sheet. Desktop: a centred modal card (photo on top, options below,
    // footer pinned to the card), instead of a full-screen page with acres of white.
    const desktopConfig = {
      panelClass: 'menu-item-desktop-dialog',
      width: '920px',
      maxWidth: '94vw',
      maxHeight: '92vh',
      autoFocus: false,
    };
    const mobileConfig = {
      panelClass: 'menu-item-fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      autoFocus: false,
    };
    const ref = this.dialog.open(MenuItemBottomSheetComponent, {
      ...(this.isDesktop ? desktopConfig : mobileConfig),
      data: sheetData,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    ref.afterClosed().subscribe(result => {
      if (result?.success) {
        this.orderUpdated.emit();
      }
    });
  }

  onChangeMenu(e) {
    this.selectedCategoryFragment = e;
    this.selectedSubCategoryFragment = null;
    localStorage.removeItem('menu_group_name');
    localStorage.removeItem('menu_sub_group_name');
  }

  onChangeSubMenu(e) {
    this.selectedSubCategoryFragment = e;
    localStorage.removeItem('menu_sub_group_name');
  }

  disabledLoadMore(itemGroup) {
    return this.storeMenu.menu_items[itemGroup].disabled
  }

  loadMore(itemGroup, subgroup) {

    this.storeMenu.menu_items[itemGroup].setCurrentOffset = this.storeMenu.menu_items[itemGroup].setCurrentOffset + this.limit;
    let menu_group_id = this.storeMenu.menu_items[itemGroup][subgroup][0]['menu_group_id']
    this.orderService.getStoreMenuV2LazyIncremental(this.store.id, this.filter, this.limit.toString(),
      this.storeMenu.menu_items[itemGroup].setCurrentOffset.toString(), menu_group_id, itemGroup, subgroup).subscribe(data => {

        if (data.length < this.limit) {
          this.storeMenu.menu_items[itemGroup].disabled = true
        }
        this.storeMenu.menu_items[itemGroup][subgroup] = this.storeMenu.menu_items[itemGroup][subgroup].concat(data)
        this.refreshSubgroupLayout(itemGroup, subgroup);
        this.cdr.markForCheck();
      })
  }

  /** Layout for one subcategory, derived from the store-level decision. */
  subgroupLayout(category: string, subCategory: string): MenuLayout {
    const key = this.getSubCategoryId(category, subCategory);
    if (!(key in this.layoutBySubgroup)) {
      this.refreshSubgroupLayout(category, subCategory);
    }
    return this.layoutBySubgroup[key];
  }

  private refreshSubgroupLayout(category: string, subCategory: string) {
    const items = this.storeMenu?.menu_items?.[category]?.[subCategory];
    const key = this.getSubCategoryId(category, subCategory);
    this.layoutBySubgroup[key] = (Array.isArray(items) && items.length > 0) ? this.storeLayout : 'list';
  }

  /** Newest 600px thumbnail for an item, or null. `last_image` is a list online and a dict on POS. */
  itemThumbnail(item: any): string | null {
    return bestListThumbnail(item?.last_image);
  }

  categoryCover(category: string): string | undefined {
    return this.categoryImages[category];
  }

  categoryItemCount(category: string): number {
    return this.categoryCounts[category] || 0;
  }

  categoryItemCountLabel(category: string): string {
    const n = this.categoryItemCount(category);
    if (!n) return '';
    return `${n}${this.categoryCountCapped[category] ? '+' : ''} ${n === 1 && !this.categoryCountCapped[category] ? 'item' : 'items'}`;
  }

  hasSubCategories(itemGroup: any): boolean {
    return itemGroup[1]?.length > 1 || (itemGroup[1]?.length === 1 && !!itemGroup[1][0]);
  }

  getSubCategoryId(category: string, subCategory: string): string {
    return `${category}___${subCategory}`;
  }

  getVisibleSubCategories(itemGroup: any): string[] {
    if (!this.hasSubCategories(itemGroup)) return [];
    return (itemGroup[1] || []).filter(sub => !!sub);
  }

  scrollToSubCategory(category: string, subCategory: string) {
    this.expandedCategories.add(category);
    this.expandedSubCategories.add(this.getSubCategoryId(category, subCategory));
    this.suspendScrollSpy();
    this.activeCategory = category;
    this.activeSubCategory = subCategory;
    this.selectedCategoryFragment = category;
    this.selectedSubCategoryFragment = subCategory;
    this.syncView();
    this.afterLayout(() => this.smoothScrollTo(this.getSubCategoryId(category, subCategory)));
  }

  /**
   * Two frames: the first lets the newly opened cards lay out, the second lets that layout settle,
   * so the scroll aims at where the heading ends up rather than where it was mid-expand.
   */
  private afterLayout(fn: () => void) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!this.destroyed) fn();
    }));
  }

  private smoothScrollTo(elementId: string) {
    const el = this.document.getElementById(elementId) as HTMLElement | null;
    if (el) {
      el.style.scrollMarginTop = this.stickyOffset() + 'px';
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.resumeScrollSpyWhenIdle();
  }

  /**
   * Cover photo per category / subcategory for the sidebar and panel chips.
   * Prefers the category's own image (`items[0].image`) and falls back to the first item
   * photo (`last_image`), so a category is only ever photo-less when none of its items has one.
   * Also counts items and seeds the per-subcategory layout map in the same pass.
   */
  private buildCategoryImages() {
    if (!this.storeMenu?.menu_items) return;
    for (const catName in this.storeMenu.menu_items) {
      const catData = this.storeMenu.menu_items[catName];
      let count = 0;
      let capped = false;
      for (const subName in catData) {
        const items = catData[subName];
        if (!Array.isArray(items)) continue;
        count += items.length;
        if (items.length >= this.limit) capped = true;
        const subKey = this.getSubCategoryId(catName, subName);
        const cover = items.length && items[0]?.image
          ? this.imageBase + items[0].image
          : this.firstItemThumbnail(items);
        if (cover) {
          this.subCategoryImages[subKey] = cover;
          if (!this.categoryImages[catName]) this.categoryImages[catName] = cover;
        }
        this.refreshSubgroupLayout(catName, subName);
      }
      this.categoryCounts[catName] = count;
      this.categoryCountCapped[catName] = capped;
    }
  }

  private firstItemThumbnail(items: any[]): string | undefined {
    for (const item of items) {
      const thumb = this.itemThumbnail(item);
      if (thumb) return thumb;
    }
    return undefined;
  }

  public santize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

}
